// MainVideoPlayer.jsx
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveVideoClip } from "../../features/videoClip/videoClipSlice";
import { ClipDescriptionModal } from "./ClipDescriptionModal";

export function MainVideoPlayer({ src, isLoading, poster, videoId, playerId, isMainVideo = true }) {
  const dispatch = useDispatch();
  const { saveLoading } = useSelector((state) => state.videoClip);
  const userRole = useSelector((state) => state.auth?.user?.role || state.auth?.role || '');
  const isPlayer = userRole?.toLowerCase() === 'player';
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  // Stopwatch state
  const [stopwatchRunning, setStopwatchRunning] = useState(false);
  const [stopwatchTime, setStopwatchTime] = useState(0); // Elapsed time in milliseconds
  const [inTime, setInTime] = useState(null); // Video position when IN was clicked
  const [outTime, setOutTime] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [inVideoPosition, setInVideoPosition] = useState(null); // Store video position when IN is clicked
  const stopwatchIntervalRef = useRef(null);
  const stopwatchStartTimeRef = useRef(null); // Track when stopwatch started

  // Run stopwatch - count elapsed time, not video time
  useEffect(() => {
    if (stopwatchRunning && stopwatchStartTimeRef.current !== null) {
      stopwatchIntervalRef.current = setInterval(() => {
        // Calculate elapsed time since stopwatch started
        const elapsedTime = Date.now() - stopwatchStartTimeRef.current;
        setStopwatchTime(elapsedTime);
      }, 10);
    } else {
      if (stopwatchIntervalRef.current) {
        clearInterval(stopwatchIntervalRef.current);
      }
    }

    return () => {
      if (stopwatchIntervalRef.current) {
        clearInterval(stopwatchIntervalRef.current);
      }
    };
  }, [stopwatchRunning]);

  useEffect(() => {
    // Reset error when src changes
    setError(null);

    // Reset stopwatch state when video changes
    setStopwatchRunning(false);
    setStopwatchTime(0);
    setInTime(null);
    setOutTime(null);
    setSaveSuccess(false);
    setInVideoPosition(null);
    stopwatchStartTimeRef.current = null;

    // Auto-play when video is loaded
    if (videoRef.current && src) {
      videoRef.current.load();
    }
  }, [src]);

  // Stopwatch handlers
  const resetStopwatch = () => {
    setStopwatchRunning(false);
    setStopwatchTime(0); // Reset stopwatch to 0
    stopwatchStartTimeRef.current = null;

    // If IN was clicked, seek video back to that position
    if (inVideoPosition !== null && videoRef.current) {
      videoRef.current.currentTime = inVideoPosition / 1000; // Convert ms to seconds
    }

    setInTime(null);
    setOutTime(null);
    setSaveSuccess(false);
    setInVideoPosition(null);
  };

  const markInTime = () => {
    // Get current video time in milliseconds
    const currentVideoTime = videoRef.current ? Math.floor(videoRef.current.currentTime * 1000) : 0;

    // Store the video position where IN was clicked
    setInVideoPosition(currentVideoTime);
    setInTime(currentVideoTime);
    setOutTime(null);
    setSaveSuccess(false);

    // Reset stopwatch to 0 and start counting
    setStopwatchTime(0);
    stopwatchStartTimeRef.current = Date.now();
    setStopwatchRunning(true);

    // Play video if paused, keep playing if already playing
    if (videoRef.current && videoRef.current.paused) {
      videoRef.current.play().catch(err => {
        console.error("Error playing video:", err);
      });
    }
  };

  const markOutTime = () => {
    // Get current video time in milliseconds (absolute position)
    const currentVideoTime = videoRef.current ? Math.floor(videoRef.current.currentTime * 1000) : 0;

    // Stop the timer (stopwatchTime already has the elapsed duration)
    setStopwatchRunning(false);
    stopwatchStartTimeRef.current = null;

    // Set outTime to current video position for saving the clip
    setOutTime(currentVideoTime);

    // Pause video
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }

    // Open description modal instead of auto-saving
    setShowDescriptionModal(true);
  };

  // Save clip to backend using Redux
  const saveClip = async (description = "") => {
    if (!videoId || !playerId) {
      console.error("Missing videoId or playerId");
      return;
    }

    if (inTime === null || outTime === null) {
      console.error("Invalid time range");
      return;
    }

    try {
      const result = await dispatch(saveVideoClip({
        videoId,
        playerId,
        inTime,
        outTime,
        duration: outTime - inTime,
        description
      })).unwrap();

      console.log("Clip saved successfully:", result);
      setSaveSuccess(true);
      setShowDescriptionModal(false);

      // Reset for next clip
      setInTime(null);
      setOutTime(null);

      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error saving clip:", error);
      alert("Failed to save clip. Please try again.");
    }
  };

  // Handle modal close without saving
  const handleModalClose = () => {
    setShowDescriptionModal(false);
    // Reset times and position so user can try again
    setInTime(null);
    setOutTime(null);
    setInVideoPosition(null);
    setStopwatchTime(0);
  };

  // Format time for display (HH:MM:SS.ms)
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const ms = Math.floor((milliseconds % 1000) / 10);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  const handleError = (e) => {
    console.error("Video loading error:", e);
    setError("Failed to load video. Please check the video URL.");
  };

  const handleLoadedData = () => {
    console.log("Video loaded successfully:", src);
  };

  return (
    <div className="bg-black rounded-sm sm:rounded-md overflow-hidden relative w-full">
      <div className="relative w-full aspect-video bg-black flex items-center justify-center">
        {isLoading ? (
          <div className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 rounded-full border-3 sm:border-4 border-slate-600 border-t-slate-200 animate-spin" />
        ) : !src ? (
          <div className="text-slate-400 text-[10px] sm:text-xs md:text-sm text-center px-3 sm:px-4">No video source provided</div>
        ) : error ? (
          <div className="text-red-400 text-[10px] sm:text-xs md:text-sm text-center px-3 sm:px-4">
            {error}
            <div className="text-[9px] sm:text-[10px] md:text-xs text-slate-500 mt-1 sm:mt-2 break-all max-w-full">URL: {src}</div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              key={src}
              src={src}
              poster={poster}
              controls
              autoPlay
              preload="metadata"
              className="h-full w-full object-contain"
              onError={handleError}
              onLoadedData={handleLoadedData}
              controlsList="nodownload"
              playsInline
            >
              <source src={src} type="video/mp4" />
              <source src={src} type="video/webm" />
              <source src={src} type="video/ogg" />
              Your browser does not support the video tag.
            </video>

            {/* Stopwatch Overlay - Only show for main video, not clips, and not for players */}
            {isMainVideo && !isPlayer && (
            <div className="absolute bottom-14 sm:bottom-16 right-1 sm:right-2 flex items-center gap-1 sm:gap-1.5 bg-black/90 backdrop-blur-sm px-1.5 sm:px-2 py-1 sm:py-1.5 rounded shadow-lg">
              {/* STOPWATCH Label */}
              <span className="text-white text-[10px] sm:text-[11px] font-semibold tracking-wide">STOPWATCH</span>

              {/* IN Button */}
              <button
                onClick={markInTime}
                className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-[11px] font-semibold border rounded transition-colors min-w-[28px] sm:min-w-[32px] ${
                  inTime !== null && outTime === null
                    ? 'bg-green-600 border-green-500 text-white'
                    : 'text-white border-white hover:bg-white hover:text-black'
                }`}
                title="Start timer & play video"
                disabled={saveLoading}
              >
                IN
              </button>

              {/* OUT Button */}
              <button
                onClick={markOutTime}
                className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-[11px] font-semibold border rounded transition-colors min-w-[28px] sm:min-w-[32px] ${
                  saveLoading
                    ? 'bg-yellow-600 border-yellow-500 text-white cursor-wait'
                    : saveSuccess
                    ? 'bg-green-600 border-green-500 text-white'
                      : 'text-white border-white hover:bg-white hover:text-black'
                }`}
                title="Stop timer, pause video & save clip"
                disabled={inTime === null || saveLoading}
              >
                {saveLoading ? '...' : 'OUT'}
              </button>

              {/* Time Display */}
              <div className="bg-black px-1.5 sm:px-2 py-0.5 sm:py-1 border border-white rounded">
                <span className="text-white text-[10px] sm:text-[11px] font-mono tabular-nums">
                  {formatTime(stopwatchTime)}
                </span>
              </div>

              {/* Reset Button */}
              <button
                onClick={resetStopwatch}
                className="px-1 sm:px-1.5 py-0.5 sm:py-1 hover:bg-white/10 border border-white rounded transition-colors"
                title="Reset stopwatch"
                disabled={saveLoading}
              >
                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
            )}

            {/* Save Success Notification - Only show for main video */}
            {isMainVideo && saveSuccess && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded shadow-lg flex items-center gap-2 animate-fade-in">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-xs sm:text-sm font-semibold">Clip Saved!</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Clip Description Modal */}
      <ClipDescriptionModal
        isOpen={showDescriptionModal}
        onClose={handleModalClose}
        onSave={saveClip}
        inTime={inTime || 0}
        outTime={outTime || 0}
        loading={saveLoading}
      />
    </div>
  );
}
